import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  RAPDataCheck,
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';
import { validateHMDDetailsVertical } from '@amzn/aet-galactus-kit';
import { convertToVerticalOption } from '../../../../../Util/Vertical';
import {
  Feature,
  isFeatureEnabled,
} from '../../../../../config/FeaturesConfig';

const DisplayRapSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
  vertical,
  skill,
  segment,
  channel,
  marketplace,
  timeOption,
  timestamp,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.RAP?.submetrics) {
    return <></>;
  }

  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);

  if (RAPDataCheck(metrics.RAP?.submetrics!)) {
    return <NoSubMetric />;
  }

  const {
    totalHmdResponses,
    hmdYesAdjustedCount,
    hmdYesResponses,
    hmdNoResponses,
    rapRepeatCount,
    prr,
    fcr,
    nonOrderTaggingRate,
    hmdOfferRate,
    hmdResponseRate,
  } = metrics.RAP?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {fcr && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_fcr'
                  defaultMessage='FCR'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_fcr_def'
                defaultMessage='First Contact Resolution (FCR) rate is the percentage of resolved contacts that did not result in a repeat contact from the customer in the following 7 days.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={fcr.datapoint} isBold isPercentage />
          <DisplaySubMetric
            value={fcr.networkAverage}
            isPercentage
            datapointNull={fcr.datapoint === null}
          />
        </Row>
      )}
      {hmdNoResponses && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          {legacyExperience ? (
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_hmdNoResponses'
                defaultMessage='HMD No Responses'
              />
            </Text>
          ) : (
            <ControlledExpander
              title={
                <Text type={SUBMETRIC_FONT_SIZE}>
                  <FormattedMessage
                    id='cs_perfportal_lighthouse_hmdNoResponses'
                    defaultMessage='HMD No Responses'
                  />
                </Text>
              }
              type='list'
            >
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_hmdNoResponses_def'
                  defaultMessage="Sum of contacts which received a 'No' response to the HMD survey."
                />
              </Text>
            </ControlledExpander>
          )}
          <DisplaySubMetric
            value={hmdNoResponses.datapoint}
            isBold
            isLink={
              validateHMDDetailsVertical(convertToVerticalOption(vertical)) &&
              !legacyExperience
            }
            vertical={vertical}
            skill={skill}
            segment={segment}
            channel={channel}
            marketplace={marketplace}
            timeOption={timeOption}
            timestamp={timestamp}
            tabRef={'hmdNo'}
            topLevelMetric={'RAP'}
          />
          {!legacyExperience && (
            <DisplaySubMetric
              value={hmdNoResponses.networkAverage}
              datapointNull={hmdNoResponses.datapoint === null}
            />
          )}
        </Row>
      )}

      {hmdOfferRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfporta_lighthouse_hmdOfferRate'
                  defaultMessage='HMD Offer Rate'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfporta_lighthouse_hmdOfferRate_def'
                defaultMessage='Percentage of contacts for which a HMD poll was sent out the total contacts resolved by the associate.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={hmdOfferRate.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={hmdOfferRate.networkAverage}
            isPercentage
            datapointNull={hmdOfferRate.datapoint === null}
          />
        </Row>
      )}
      {hmdResponseRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfporta_lighthouse_hmdResponseRate'
                  defaultMessage='HMD Response Rate'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfporta_lighthouse_hmdResponseRate_def'
                defaultMessage='Percentage of contacts which received a "Yes" or "No" response to the HMD survey out the total contacts resolved by the associate with a HMD poll sent.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={hmdResponseRate.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={hmdResponseRate.networkAverage}
            isPercentage
            datapointNull={hmdResponseRate.datapoint === null}
          />
        </Row>
      )}

      {totalHmdResponses && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_totalHMDResponses'
                  defaultMessage='HMD Responses'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_totalHMDResponses_def'
                defaultMessage='Sum of contacts which received a response to the HMD survey.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={totalHmdResponses.datapoint} isBold />
          <DisplaySubMetric
            value={totalHmdResponses.networkAverage}
            datapointNull={totalHmdResponses.datapoint === null}
          />
        </Row>
      )}

      {hmdYesAdjustedCount && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          {legacyExperience ? (
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_hmdYesAdjusted'
                defaultMessage='HMD Yes Adjusted'
              />
            </Text>
          ) : (
            <ControlledExpander
              title={
                <Text type={SUBMETRIC_FONT_SIZE}>
                  <FormattedMessage
                    id='cs_perfportal_lighthouse_hmdYesAdjusted'
                    defaultMessage='HMD Yes Adjusted'
                  />
                </Text>
              }
              type='list'
            >
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_hmdYesAdjusted_def'
                  defaultMessage="Sum of contacts which received a 'Yes' response to the HMD survey, after adjustment for repeat contacts in the following 48 hours."
                />
              </Text>
            </ControlledExpander>
          )}
          <DisplaySubMetric value={hmdYesAdjustedCount.datapoint} isBold />
          {!legacyExperience && (
            <DisplaySubMetric
              value={hmdYesAdjustedCount.networkAverage}
              datapointNull={hmdYesAdjustedCount.datapoint === null}
            />
          )}
        </Row>
      )}
      {/* HMD Yes Responses are only available for Consumer */}
      {hmdYesResponses && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_hmdYesResponses'
                  defaultMessage='HMD Yes Responses'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_hmdYesResponses_def'
                defaultMessage="Sum of contacts which received a 'Yes' response to the HMD survey."
              />
            </Text>
          </ControlledExpander>

          <DisplaySubMetric
            value={hmdYesResponses.datapoint}
            isBold
            isLink={validateHMDDetailsVertical(
              convertToVerticalOption(vertical)
            )}
            vertical={vertical}
            skill={skill}
            segment={segment}
            channel={channel}
            marketplace={marketplace}
            timeOption={timeOption}
            timestamp={timestamp}
            tabRef={'hmdYes'}
            topLevelMetric={'RAP'}
          />
          <DisplaySubMetric
            value={hmdYesResponses.networkAverage}
            datapointNull={hmdYesResponses.datapoint === null}
          />
        </Row>
      )}

      {nonOrderTaggingRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_nonOrderTaggingRate'
                  defaultMessage='Non Order Tagging Rate'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_nonOrderTaggingRate_def'
                defaultMessage={`Non Order Tagging (NOT) rate is the percentage of 'Order Related' (SIC1) HMD responses that did not have an order ID associated.`}
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={nonOrderTaggingRate.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={nonOrderTaggingRate.networkAverage}
            isPercentage
            datapointNull={nonOrderTaggingRate.datapoint === null}
          />
        </Row>
      )}

      {prr && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage id='cs_perfportal_prr' defaultMessage='PRR' />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_prr_def'
                defaultMessage={`Positive Response Rate (PRR) is the percentage of 'Yes' responses out of the total contacts with responses to the How's My Driving (HMD) survey.`}
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={prr.datapoint} isPercentage isBold />
          <DisplaySubMetric
            value={prr.networkAverage}
            isPercentage
            datapointNull={prr.datapoint === null}
          />
        </Row>
      )}

      {rapRepeatCount && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          {legacyExperience ? (
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_rapRepeatCount'
                defaultMessage='RAP Repeat Count'
              />
            </Text>
          ) : (
            <ControlledExpander
              title={
                <Text type={SUBMETRIC_FONT_SIZE}>
                  <FormattedMessage
                    id='cs_perfportal_lighthouse_rapRepeatCount'
                    defaultMessage='RAP Repeat Count'
                  />
                </Text>
              }
              type='list'
            >
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_rapRepeatCount_def'
                  defaultMessage="Sum of contacts which received a 'Yes' response to the HMD survey and for which a repeat contact occurred in the following 48 hours."
                />
              </Text>
            </ControlledExpander>
          )}
          <DisplaySubMetric
            value={rapRepeatCount.datapoint}
            isBold
            isLink={validateHMDDetailsVertical(
              convertToVerticalOption(vertical)
            )}
            vertical={vertical}
            skill={skill}
            segment={segment}
            channel={channel}
            marketplace={marketplace}
            timeOption={timeOption}
            timestamp={timestamp}
            tabRef={'repeat'}
            topLevelMetric={'RAP'}
          />
          {!legacyExperience && (
            <DisplaySubMetric
              value={rapRepeatCount.networkAverage}
              datapointNull={rapRepeatCount.datapoint === null}
            />
          )}
        </Row>
      )}
    </Column>
  );
};

export default DisplayRapSubMetrics;
